import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { motion } from "framer-motion";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [guideUrl, setGuideUrl] = useState(null);
  const [buyerEmail, setBuyerEmail] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n >= 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setGuideUrl(data.guide_url || null);
          setBuyerEmail(data.customer_email || null);
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTimeout(() => poll(n + 1), 2500);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="success-page" className="min-h-screen flex items-center justify-center px-6 py-24" style={{ background: "#FAF9F6" }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-xl w-full text-center"
      >
        <span className="overline" data-testid="success-overline">Findaura Ventures</span>
        <h1 className="font-serif text-5xl md:text-6xl font-light mt-6 leading-tight">
          {status === "paid" && <>Welcome to the <em>Soft Life</em>.</>}
          {status === "checking" && <>Just a <em>moment</em>…</>}
          {status === "timeout" && <>Still <em>processing</em>…</>}
          {status === "expired" && <>Session <em>expired</em>.</>}
          {status === "missing" && <>Nothing to see <em>here</em>.</>}
        </h1>
        <div className="mt-8 mx-auto gold-hair" />

        <p className="mt-8 text-[15px] leading-relaxed text-[#5C5A56] max-w-md mx-auto">
          {status === "paid" && "Thank you for your purchase. Your copy of The Soft Life AI Guide is ready to download below."}
          {status === "checking" && "We're confirming your payment with Stripe. Attempts: " + attempts}
          {status === "timeout" && "Payment is still confirming. You'll receive an email once it's complete."}
          {status === "expired" && "Your checkout session expired before payment. You can try again from the home page."}
          {status === "missing" && "No checkout session found in this link."}
        </p>

        {status === "paid" && buyerEmail && (
          <p className="mt-4 text-sm text-[#8A867F]" data-testid="delivery-email-note">
            A copy has also been sent to <span className="text-[#1A1A1A]">{buyerEmail}</span>.
          </p>
        )}

        {status === "paid" && guideUrl && (
          <a
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-10 inline-flex"
            data-testid="download-guide-button"
          >
            Download the Guide
            <span aria-hidden>↓</span>
          </a>
        )}

        <Link to="/" data-testid="return-home" className="btn-outline mt-12 inline-block">Return home</Link>
      </motion.div>
    </div>
  );
}
